import MarkdownIt from 'markdown-it';
import { Book } from '../database.types';

const md = new MarkdownIt({
  html: true,
  breaks: true,
  typographer: true
});

// ---- Shared block parser ----

export interface MarkdownBlock {
  type: 'paragraph' | 'heading' | 'bullet' | 'numbered-list';
  level?: 1 | 2 | 3;
  text?: string;        // for paragraph / heading (inline markers preserved)
  items?: string[];     // for bullet / numbered-list
}

/**
 * Parse generated_text (markdown) into structured blocks.
 * Handles ## headings, **bold**, *italic*, bullet lists, numbered lists, paragraphs.
 */
export function parseMarkdownBlocks(raw: string): MarkdownBlock[] {
  const blocks: MarkdownBlock[] = [];
  const lines = raw.split('\n');

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Skip blank lines
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Headings
    const headingMatch = line.match(/^(#{1,3})\s+(.+)$/);
    if (headingMatch) {
      blocks.push({
        type: 'heading',
        level: headingMatch[1].length as 1 | 2 | 3,
        text: headingMatch[2].trim(),
      });
      i++;
      continue;
    }

    // Bullet list (-, *, +)
    if (/^\s*[-*+]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*+]\s+/, '').trim());
        i++;
      }
      blocks.push({ type: 'bullet', items });
      continue;
    }

    // Numbered list
    if (/^\s*\d+[.)]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+[.)]\s+/, '').trim());
        i++;
      }
      blocks.push({ type: 'numbered-list', items });
      continue;
    }

    // Paragraph — collect consecutive non-blank, non-special lines
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^#{1,3}\s+/.test(lines[i]) &&
      !/^\s*[-*+]\s+/.test(lines[i]) &&
      !/^\s*\d+[.)]\s+/.test(lines[i])
    ) {
      paraLines.push(lines[i].trim());
      i++;
    }
    if (paraLines.length > 0) {
      blocks.push({ type: 'paragraph', text: paraLines.join(' ') });
    }
  }

  return blocks;
}

/**
 * Parse inline markdown (bold, italic) into segments.
 * Returns array of { text, bold, italic }.
 */
export interface InlineSegment {
  text: string;
  bold: boolean;
  italic: boolean;
}

export function parseInlineFormatting(text: string): InlineSegment[] {
  const segments: InlineSegment[] = [];
  // Match **bold**, *italic*, ***bold+italic***, or plain text
  const regex = /(\*{1,3})((?:(?!\1).)+?)\1|([^*]+)/g;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match[3] !== undefined) {
      // Plain text
      if (match[3]) segments.push({ text: match[3], bold: false, italic: false });
    } else {
      const stars = match[1].length;
      const content = match[2];
      segments.push({
        text: content,
        bold: stars >= 2,
        italic: stars === 1 || stars === 3,
      });
    }
  }

  // Fallback if regex matched nothing
  if (segments.length === 0 && text) {
    segments.push({ text, bold: false, italic: false });
  }

  return segments;
}

// ---- Legacy exports (keep existing API) ----

export function convertToMarkdown(book: Book): string {
  let markdown = `# ${book.title}\n\n`;

  if (book.subtitle) {
    markdown += `## ${book.subtitle}\n\n`;
  }

  if (book.author) {
    markdown += `by ${book.author}\n\n`;
  }

  // Copyright page
  markdown += `\u00A9 ${new Date().getFullYear()} ${book.author || 'All rights reserved'}\n\n`;
  markdown += 'All rights reserved.\n\n';

  // Author bio
  if (book.author_bio) {
    markdown += '## About the Author\n\n';
    markdown += `${book.author_bio}\n\n`;
  }

  // Filter and sort chapters
  const filteredChapters = (book.chapters || [])
    .filter(ch => {
      const t = ch.chapter_title.trim();
      return !/^(?:purpose|goal|objective|note|description|overview|summary|intent|aim)\s*[:*]/i.test(t);
    })
    .sort((a, b) => {
      if (a.type === 'conclusion' && b.type !== 'conclusion') return 1;
      if (b.type === 'conclusion' && a.type !== 'conclusion') return -1;
      if (a.type === 'introduction' && b.type !== 'introduction') return -1;
      if (b.type === 'introduction' && a.type !== 'introduction') return 1;
      return a.order_index - b.order_index;
    });

  // Table of Contents
  markdown += '## Table of Contents\n\n';
  filteredChapters.forEach(chapter => {
    markdown += `* ${chapter.chapter_title}\n`;
  });
  markdown += '\n\n';

  // Chapters
  filteredChapters.forEach(chapter => {
      markdown += `# ${chapter.chapter_title}\n\n`;
      markdown += `${chapter.generated_text}\n\n`;
    });

  return markdown;
}

export function markdownToHtml(markdown: string): string {
  return md.render(markdown);
}
